'use client';

import React from 'react';
import { buildBookingLinkFromData } from '@/lib/affiliates/builders/booking';
import AffiliateLink from '@/components/common/AffiliateLink';
import { getProviderButtonStyles } from './providerButtonStyles';

/**
 * Компонент кнопки/ссылки для бронирования на Booking.com через Travelpayouts
 * 
 * Генерирует партнёрскую ссылку:
 * https://tp.media/r?marker=633678&p=2076&u=<ENCODED_BOOKING_URL>&sub_id=<метка>
 */
export default function BookingLinkButton({
    children,           // React children (содержимое кнопки)
    className = '',
    variant = 'primary', // 'primary', 'secondary', 'chip', 'outline'
    cityName,           // Название города на латинице (например, "Zurich")
    countryName,        // Название страны на латинице (например, "Switzerland")
    countryCurrency,    // Валюта страны из Sanity (например, "EUR", "CHF", "USD")
    citySlug,           // Слаг города (например, "zurich")
    siteLang,           // Язык сайта ('ru' или 'en')
    checkin,            // Дата заезда YYYY-MM-DD (опционально)
    checkout,           // Дата выезда YYYY-MM-DD (опционально)
    adults,             // Количество взрослых (опционально)
    childrenCount,      // Количество детей-гостей (переименовано чтобы не конфликтовать с React children)
    rooms,              // Количество комнат (опционально)
    subIdSuffix = 'hero', // Суффикс для subId
    onClick,
    disabled = false,
    ...props
}) {
    // Генерируем ссылку
    const href = buildBookingLinkFromData({
        cityName,
        countryName,
        countryCurrency,
        citySlug,
        siteLang,
        checkin,
        checkout,
        adults,
        childrenCount,
        rooms,
        subIdSuffix
    });

    // Аналитика клика целиком в AffiliateLink (единый affiliate_click с
    // vertical='stay'). Отдельный booking_click убран 2026-08-11 (аудит §4.5):
    // три имени события на одну воронку жилья прятали реальные ~48 кликов/90д
    // под affiliate_click, а в дашборде было видно «3 бронирования».
    // subId и так вшит в href (tp.media sub_id) — виден в статистике Travelpayouts.
    const handleClick = (e) => {
        if (onClick) {
            onClick(e);
        }
    };

    const variantStyles = getProviderButtonStyles(variant, 'booking');

    // Если кнопка отключена или нет данных для ссылки
    if (disabled || !cityName) {
        return (
            <button
                className={`${variantStyles} ${className} opacity-50 cursor-not-allowed`}
                disabled
                {...props}
            >
                {children}
            </button>
        );
    }

    // Рендерим как ссылку
    return (
        <AffiliateLink
            href={href}
            provider="booking"
            vertical="stay"
            position="listing"
            className={`${variantStyles} ${className} no-underline`}
            onClick={handleClick}
            {...props}
        >
            {children}
        </AffiliateLink>
    );
}

/**
 * Иконка Booking.com (опциональная)
 */
export function BookingIcon({ className = 'w-5 h-5' }) {
    return (
        <svg 
            className={className} 
            viewBox="0 0 24 24" 
            fill="currentColor"
        >
            <path d="M2.273 6.364h4.09c1.455 0 2.455.273 3.091.818.637.546.955 1.364.955 2.455 0 .727-.182 1.318-.546 1.773-.363.454-.909.772-1.636.954v.046c.909.136 1.591.454 2.046.954.454.5.682 1.182.682 2.046 0 1.182-.41 2.091-1.228 2.727-.818.637-1.955.955-3.409.955H2.273V6.364zm2.455 5h1.636c.727 0 1.227-.136 1.545-.409.318-.273.477-.682.477-1.227 0-.5-.182-.864-.546-1.091-.363-.227-.909-.341-1.636-.341H4.727v3.068zm0 1.818v3.364h1.909c.727 0 1.273-.159 1.636-.477.364-.318.546-.773.546-1.364 0-.591-.182-1.023-.546-1.296-.363-.272-.909-.227-1.636-.227H4.727z"/>
        </svg>
    );
}

